import { inject, Injectable } from "@angular/core";
import { RiskMapTreeColumns, ComparatorOperators, GetRiskMapTreeGQL, GetRiskMapTreeQuery, OperatorInput, OrderTypeInput, RiskMapTreeQueryComplexInput, RiskMapTreeSortInput, TreeAuImpactGQL, TreeAuImpactQuery, UpdateTreeAuGQL, UpdateTreeAuMutation, RiskMapTreeSimpleQueryInput, HandleRiskMapTreeGQL, HandleRiskMapTreeMutation, CreateRiskMapTreeGQL, UpdateRiskMapTreeGQL, CreateRiskMapTreeMutation, UpdateRiskMapTreeMutation, UpdateTreeStatusGQL, UpdateTreeStatusMutation, ExportRiskmapTreeGQL, ExportRiskmapTreeMutation, ProcessRelevanceTreeGQL, ProcessRelevanceTreeMutation, PagingInput, GetRiskMapTreeFiltersGQL, GetRiskMapTreeFiltersQuery, UpdateTreeNoteRiskMapGQL, UpdateTreeNoteRiskMapMutation } from "../../../graphql/generated";
import { map, Observable } from "rxjs";
import { MutationData, unwrapMutationData } from "../shared/graphql-mutation-response.util";

/** Una audit entity che il cambio AU scollegherebbe dalle proposals a cui è legata. */
export type TreeAuImpact = NonNullable<NonNullable<NonNullable<TreeAuImpactQuery['treeAuImpact']>['data']>[number]>;

@Injectable({
    providedIn: 'root'
})

export class RiskMapTreeService {
    private readonly GetRiskMapTreeGQL: GetRiskMapTreeGQL = inject(GetRiskMapTreeGQL);
    private readonly GetRiskMapTreeFiltersGQL: GetRiskMapTreeFiltersGQL = inject(GetRiskMapTreeFiltersGQL)
    private readonly HandleRiskMapTreeGQL: HandleRiskMapTreeGQL = inject(HandleRiskMapTreeGQL);
    private readonly TreeAuImpactGQL: TreeAuImpactGQL = inject(TreeAuImpactGQL);
    private readonly UpdateTreeAuGQL: UpdateTreeAuGQL = inject(UpdateTreeAuGQL);
    private readonly CreateRiskMapTreeGQL: CreateRiskMapTreeGQL = inject(CreateRiskMapTreeGQL);
    private readonly UpdateRiskMapTreeGQL: UpdateRiskMapTreeGQL = inject(UpdateRiskMapTreeGQL);
    private readonly UpdateTreeStatusGQL: UpdateTreeStatusGQL = inject(UpdateTreeStatusGQL);
    private readonly UpdateTreeNoteRiskMapGQL: UpdateTreeNoteRiskMapGQL = inject(UpdateTreeNoteRiskMapGQL);
    private readonly ExportRiskmapTreeGQL: ExportRiskmapTreeGQL = inject(ExportRiskmapTreeGQL);
    private readonly ProcessRelevanceTreeGQL: ProcessRelevanceTreeGQL = inject(ProcessRelevanceTreeGQL)

    getRiskMapTree(
        campaignId: string,
        legalEntityId: string,
        parameters: any,
        au: boolean | null
    ): Observable<GetRiskMapTreeQuery['riskMapTreeByLegalEntityIdAndCampaignId']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetRiskMapTreeGQL.fetch({ campaignId, legalEntityId, query: param.query as RiskMapTreeQueryComplexInput, sort: param.sort as RiskMapTreeSortInput, au, page: param.page }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTreeByLegalEntityIdAndCampaignId))
    }

    getRiskMapTreeFilters(
        campaignId: string,
        legalEntityId: string,
        au: boolean | null
    ): Observable<GetRiskMapTreeFiltersQuery['riskMapTreeFields']> {
        return this.GetRiskMapTreeFiltersGQL.fetch({ campaignId, legalEntityId, au: null, active: null }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTreeFields))
    }

    getRiskMapTreeAu(
        campaignId: string,
        legalEntityId: string,
        parameters: any
    ): Observable<GetRiskMapTreeQuery['riskMapTreeByLegalEntityIdAndCampaignId']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetRiskMapTreeGQL.fetch({ campaignId, legalEntityId, query: param.query as RiskMapTreeQueryComplexInput, sort: param.sort as RiskMapTreeSortInput }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTreeByLegalEntityIdAndCampaignId))
    }

    handleRiskMapTree(
        riskMapId: string | null,
        campaignId: string,
        legalEntityId: string,
        processId: string,
        riskId: string,
        value: boolean,
        filter: boolean
    ): Observable<MutationData<HandleRiskMapTreeMutation['handleRiskMapTree']>> {
        return this.HandleRiskMapTreeGQL.mutate({ campaignId, legalEntityId, processId, riskId, riskMapId, value, filter }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.handleRiskMapTree), unwrapMutationData('Unable to update the risk map'))
    }

    /**
     * Preview in sola lettura di `updateTreeAu`: le audit entity che il cambio AU
     * scollegherebbe dalle loro proposals. Lista vuota = nessun impatto, la mutation
     * può partire senza chiedere conferma.
     */
    treeAuImpact(
        campaignId: string,
        legalEntityId: string,
        processId: string
    ): Observable<TreeAuImpact[]> {
        return this.TreeAuImpactGQL.fetch({ campaignId, legalEntityId, processId }, { fetchPolicy: 'no-cache' }).pipe(
            map(res => res.data.treeAuImpact),
            unwrapMutationData('Unable to check the auditable universe impact'),
            map(impacts => (impacts ?? []).filter((impact): impact is TreeAuImpact => impact != null))
        )
    }

    updateTreeAu(
        campaignId: string,
        legalEntityId: string,
        processId: string,
        filter: boolean
    ): Observable<MutationData<UpdateTreeAuMutation['updateTreeAu']>> {
        return this.UpdateTreeAuGQL.mutate({ campaignId, legalEntityId, processId, filter }, { fetchPolicy: 'no-cache' }).pipe(map(res => (res.data?.updateTreeAu)), unwrapMutationData('Unable to update the auditable universe'))
    }

    createRiskMapTree(
        campaignId: string,
        legalEntityId: string,
        processId: string,
        riskId: string
    ): Observable<CreateRiskMapTreeMutation['createRiskMapTree']> {
        return this.CreateRiskMapTreeGQL.mutate({ campaignId, legalEntityId, processId, riskId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createRiskMapTree))
    }

    updateRiskMapTree(
        riskId: string,
        riskMapId: string,
        state: boolean
    ): Observable<UpdateRiskMapTreeMutation['updateRiskMapTreeState']> {
        return this.UpdateRiskMapTreeGQL.mutate({ riskId, riskMapId, state }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateRiskMapTreeState))
    }

    updateTreeStatus(
        campaignId: string,
        legalEntityId: string,
        processId: string,
        status: boolean,
        filter: boolean
    ): Observable<MutationData<UpdateTreeStatusMutation['updateRiskMapStatusTree']>> {
        return this.UpdateTreeStatusGQL.mutate({ campaignId, legalEntityId, processId, status, filter }, { fetchPolicy: 'no-cache' }).pipe(map(ret => ret.data?.updateRiskMapStatusTree), unwrapMutationData('Unable to update the status'))
    }

    updateTreeNote(
        campaignId: string,
        legalEntityId: string,
        processCode: string,
        note: string,
        filter: boolean
    ): Observable<MutationData<UpdateTreeNoteRiskMapMutation['updateTreeNote']>> {
        return this.UpdateTreeNoteRiskMapGQL.mutate({ campaignId, legalEntityId, processCode, note, filter, active: true }, { fetchPolicy: 'no-cache' }).pipe(map(res => (res.data?.updateTreeNote)), unwrapMutationData('Unable to update the note'))
    }

    exportRiskmapTree(
        campaignId: string,
        legalEntityId: string,
        parameters: any,
        timeZone: string
    ): Observable<ExportRiskmapTreeMutation['downloadRiskMapTree']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.ExportRiskmapTreeGQL.mutate({ campaignId, legalEntityId, timeZone, query: param.query as RiskMapTreeQueryComplexInput, sort: param.sort as RiskMapTreeSortInput }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadRiskMapTree))
    }

    processRelevanceTree(campaignId: string, legalEntityId: string, processId: string): Observable<MutationData<ProcessRelevanceTreeMutation['processRelevanceTree']>> {
        return this.ProcessRelevanceTreeGQL.mutate({ campaignId, legalEntityId, processId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.processRelevanceTree), unwrapMutationData('Unable to process the relevance'))
    }
}


export function convertLazyLoadToComplexWithoutPage(parameters: any): { query: RiskMapTreeQueryComplexInput, sort: RiskMapTreeSortInput, page: PagingInput } {
    const query: RiskMapTreeSimpleQueryInput[] = [];
    const queryRisk: RiskMapTreeSimpleQueryInput[] = [];

    const fieldsNotBoolean = ['level','name', 'code', 'cluster', 'note', 'processNote', 'status', 'processStatus', 'auditArea', 'auditSubArea'];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                if (values === null || !Array.isArray(values) || values.length === 0) return;
                (ENUM_RISKMAP[field as keyof typeof ENUM_RISKMAP] || ENUM_RISKMAP.riskParameterName).forEach(key => {
                    if (key === 'RISK_PARAMETER_NAME') return;
                    const filteredValue: any[] = fieldsNotBoolean.includes(field)
                        ? values
                        : values.length > 1 ? [] : values[0] === 'Active' || values[0] === 'Yes' ? ['true'] : ['false'];
                    query.push({
                        field: key as RiskMapTreeColumns,
                        filters: {
                            operator: key === RiskMapTreeColumns.Level ? ComparatorOperators.Eq : ComparatorOperators.Match,
                            value: filteredValue
                        }
                    });
                });
            });

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values === null || !Array.isArray(values) || values.length === 0) return;
                (ENUM_RISKMAP[field as keyof typeof ENUM_RISKMAP] || ENUM_RISKMAP.riskParameterName).forEach(key => {

                    let filteredValue: any[] = [];
                    if (key === 'RISK_PARAMETER_NAME' && values.length === 1) {
                        filteredValue = [field, values[0] === 'Active' || values[0] === 'Yes' ? 'true' : 'false']
                    }
                    if (key === 'RISK_PARAMETER_NAME' && values.length === 1)
                        queryRisk.push({
                            field: key as RiskMapTreeColumns,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: filteredValue
                            }
                        });
                })
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                },
                {
                    query: queryRisk,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_RISKMAP?.[parameters?.order?.sortField as keyof typeof ENUM_RISKMAP]?.[0] as RiskMapTreeColumns || RiskMapTreeColumns.Sort,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 3
        }
    }
}

export const ENUM_RISKMAP = {
    level: [RiskMapTreeColumns.Level],
    name: [RiskMapTreeColumns.Name],
    code: [RiskMapTreeColumns.Code],
    cluster: [RiskMapTreeColumns.Cluster],
    note: [RiskMapTreeColumns.Note],
    processNote: [RiskMapTreeColumns.Note],
    au: [RiskMapTreeColumns.Au],
    auditableUniverse: [RiskMapTreeColumns.Au],
    status: [RiskMapTreeColumns.RiskMapStatus],
    riskMapStatus: [RiskMapTreeColumns.RiskMapStatus],
    riskParameterName: ['RISK_PARAMETER_NAME'],
    auditArea: [RiskMapTreeColumns.AuditDomain],
    auditSubArea: [RiskMapTreeColumns.AuditSubdomain]
}